import { useCallback, useEffect, useRef, useState } from 'react';
import { prepareZXingModule, readBarcodes } from 'zxing-wasm/reader';
import zxingReaderWasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url';

prepareZXingModule({
  overrides: {
    locateFile: (path: string, prefix: string) =>
      path.endsWith('.wasm') ? zxingReaderWasmUrl : prefix + path,
  },
});

const DEFAULT_ZOOM = 1;

type AdvancedCameraConstraint = MediaTrackConstraintSet & { torch?: boolean; zoom?: number };
type ExtendedCameraCapabilities = MediaTrackCapabilities & {
  torch?: boolean;
  zoom?: { min: number; max: number; step: number };
};
type Canvas2D = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

// GS1 DataBar / DataBar Expanded barcodes carry Application Identifiers.
// AI (01) holds a 14-digit GTIN; a leading zero means it encodes an EAN-13.
// Accepts HRI form "(01)xxxxxxxxxxxxxx" and raw leading form "01xxxxxxxxxxxxxx".
const extractEanFromResult = (text: string): string => {
  const cleaned = text.replace(/^\][A-Za-z]\d/, '');
  const gtin = cleaned.match(/\(01\)(\d{14})/)?.[1] ?? cleaned.match(/^01(\d{14})/)?.[1];
  if (!gtin) return text;
  return gtin.startsWith('0') ? gtin.slice(1) : gtin;
};

// On Androids with multiple rear lenses (Samsung S23, Pixel Pro, etc.)
// `facingMode: 'environment'` sometimes resolves to an ultra-wide sensor,
// which is unsuited for close-up barcode scanning. After permission is
// granted we enumerate devices (labels become readable) and prefer a
// back camera whose label:
//   1. Does NOT explicitly mention ultra-wide / telephoto / macro.
//   2. Has the lowest "camera N" number — Android Camera2 API convention
//      assigns the main rear sensor to the lowest ID (usually 0).
const BACK_LABEL_PATTERN = /back|environment|rear|tylna|tył/i;
const NON_MAIN_LABEL_PATTERN = /ultra[-\s]?wide|wide[-\s]?angle|\btele[a-z]*|\bzoom|\bmacro/i;

const getCameraNumber = (label: string): number => {
  const match = label.match(/camera\s+(\d+)/i);
  return match ? parseInt(match[1], 10) : Number.POSITIVE_INFINITY;
};

const shortId = (id: string | undefined) => (id ? `${id.slice(0, 8)}…${id.slice(-4)}` : '—');

const pickMainBackCameraId = async (
  currentDeviceId: string,
  log: (line: string) => void
): Promise<string | null> => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoInputs = devices.filter((d) => d.kind === 'videoinput');
    log(`found ${videoInputs.length} videoinput(s):`);
    videoInputs.forEach((d, i) =>
      log(`  [${i}] ${shortId(d.deviceId)} "${d.label || '(no label)'}"`)
    );
    log(`initial track deviceId: ${shortId(currentDeviceId)}`);

    const backs = videoInputs.filter((d) => BACK_LABEL_PATTERN.test(d.label));
    if (backs.length <= 1) {
      log(`only ${backs.length} back camera(s), no switch`);
      return null;
    }

    // Drop any back lens clearly labelled as not-main, then sort by camera number.
    const candidates = backs
      .filter((d) => !NON_MAIN_LABEL_PATTERN.test(d.label))
      .sort((a, b) => getCameraNumber(a.label) - getCameraNumber(b.label));

    const main = candidates[0];
    if (!main) {
      log('no main back camera candidate after filtering');
      return null;
    }
    if (main.deviceId === currentDeviceId) {
      log(`already on lowest-numbered back camera: "${main.label}"`);
      return null;
    }
    log(`switching to: "${main.label}"`);
    return main.deviceId;
  } catch (err) {
    log(`pickMainBackCameraId failed: ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
};

export interface UseBarcodeScannerOptions {
  onDetected: (ean: string) => void | Promise<void>;
  onReleaseRequest?: () => void;
  restartOnPopstatePath?: string;
}

export interface UseBarcodeScannerResult {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isCameraActive: boolean;
  isScanning: boolean;
  isSearchingCamera: boolean;
  isTorchSupported: boolean;
  isTorchOn: boolean;
  error: string | null;
  debugLines: string[];
  toggleTorch: () => Promise<void>;
  stop: () => Promise<void>;
}

const createCanvas = (width: number, height: number): OffscreenCanvas | HTMLCanvasElement => {
  if (typeof OffscreenCanvas !== 'undefined') {
    return new OffscreenCanvas(width, height);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

const applyTrackConstraints = async (
  track: MediaStreamTrack,
  advanced: AdvancedCameraConstraint
) => {
  await track.applyConstraints({ advanced: [advanced] } as MediaTrackConstraints);
};

export const useBarcodeScanner = (options: UseBarcodeScannerOptions): UseBarcodeScannerResult => {
  const { onDetected, onReleaseRequest, restartOnPopstatePath } = options;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<OffscreenCanvas | HTMLCanvasElement | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const pausedRef = useRef(false);
  const stoppedRef = useRef(false);
  const startingRef = useRef(false);
  const onDetectedRef = useRef(onDetected);
  const onReleaseRequestRef = useRef(onReleaseRequest);
  const isCameraActiveRef = useRef(false);
  const isTorchOnRef = useRef(false);

  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [isSearchingCamera, setIsSearchingCamera] = useState(false);
  const [isTorchSupported, setIsTorchSupported] = useState(false);
  const [isTorchOn, setIsTorchOn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debugLines, setDebugLines] = useState<string[]>([]);

  const pushDebug = useCallback((line: string) => {
    // eslint-disable-next-line no-console
    console.log('[useBarcodeScanner]', line);
    setDebugLines((prev) => [...prev, line]);
  }, []);

  useEffect(() => {
    onDetectedRef.current = onDetected;
  }, [onDetected]);
  useEffect(() => {
    onReleaseRequestRef.current = onReleaseRequest;
  }, [onReleaseRequest]);
  useEffect(() => {
    isCameraActiveRef.current = isCameraActive;
  }, [isCameraActive]);
  useEffect(() => {
    isTorchOnRef.current = isTorchOn;
  }, [isTorchOn]);

  const getTrack = (): MediaStreamTrack | null => streamRef.current?.getVideoTracks()[0] ?? null;

  const scanFrame = useCallback(async () => {
    const schedule = () => {
      if (!stoppedRef.current) {
        rafIdRef.current = requestAnimationFrame(scanFrame);
      }
    };

    if (stoppedRef.current) return;

    const video = videoRef.current;
    if (!video || video.readyState < 2 || video.videoWidth === 0) {
      schedule();
      return;
    }

    if (pausedRef.current) {
      schedule();
      return;
    }

    const w = video.videoWidth;
    const h = video.videoHeight;

    if (!canvasRef.current || canvasRef.current.width !== w || canvasRef.current.height !== h) {
      canvasRef.current = createCanvas(w, h);
    }

    const ctx = canvasRef.current.getContext('2d') as Canvas2D | null;
    if (!ctx) {
      schedule();
      return;
    }

    ctx.drawImage(video, 0, 0, w, h);
    const imageData = ctx.getImageData(0, 0, w, h);

    try {
      const results = await readBarcodes(imageData, {
        tryHarder: true,
        formats: ['EAN13', 'EAN8', 'DataBar', 'DataBarExp', 'DataBarExpStk'],
        maxNumberOfSymbols: 1,
      });
      if (results.length > 0 && !stoppedRef.current && !pausedRef.current && results[0].text) {
        pausedRef.current = true;
        try {
          await onDetectedRef.current(extractEanFromResult(results[0].text));
        } finally {
          pausedRef.current = false;
        }
      }
    } catch {
      // per-frame failures are expected; keep scanning
    }

    schedule();
  }, []);

  const applyCameraFeatures = async (track: MediaStreamTrack) => {
    const caps = (track.getCapabilities?.() ?? {}) as ExtendedCameraCapabilities;
    setIsTorchSupported(!!caps.torch);

    // On Samsung Galaxy S23 (and other Androids with logical multi-camera), the
    // default zoom may land on the ultra-wide lens (0.6x). Forcing zoom to 1x
    // pins the main sensor. Clamped to the device's supported range, so on
    // cameras where 1x is already native or out of range this is a no-op.
    if (caps.zoom) {
      const target = Math.min(Math.max(DEFAULT_ZOOM, caps.zoom.min), caps.zoom.max);
      if (Number.isFinite(target)) {
        try {
          await applyTrackConstraints(track, { zoom: target });
        } catch (zoomErr) {
          console.warn('Could not apply default zoom:', zoomErr);
        }
      }
    }
  };

  const stop = useCallback(async () => {
    stoppedRef.current = true;

    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    const track = getTrack();
    if (track && isTorchOnRef.current) {
      try {
        await applyTrackConstraints(track, { torch: false });
      } catch {
        // ignore — track may already be stopped
      }
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    canvasRef.current = null;
    pausedRef.current = false;
    setIsCameraActive(false);
    setIsTorchOn(false);
    setIsTorchSupported(false);
  }, []);

  const start = useCallback(async () => {
    if (startingRef.current || streamRef.current) return;

    startingRef.current = true;
    stoppedRef.current = false;
    setError(null);
    setIsScanning(true);
    setIsSearchingCamera(true);
    setDebugLines([]);

    try {
      await new Promise((r) => setTimeout(r, 0));
      if (stoppedRef.current) return;

      let stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });

      const initialSettings = stream.getVideoTracks()[0].getSettings() as MediaTrackSettings & {
        zoom?: number;
      };
      pushDebug(
        `facingMode:env → ${initialSettings.width}×${initialSettings.height} zoom=${
          initialSettings.zoom ?? '—'
        }`
      );

      // Permission is granted now → labels in enumerateDevices are populated.
      // If we landed on an ultra-wide/tele lens, switch to the labelled main.
      const initialDeviceId = initialSettings.deviceId ?? '';
      const mainDeviceId = await pickMainBackCameraId(initialDeviceId, pushDebug);
      if (mainDeviceId && !stoppedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: mainDeviceId },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
      }

      if (stoppedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;

      const track = stream.getVideoTracks()[0];
      const finalSettings = track.getSettings() as MediaTrackSettings & { zoom?: number };
      const finalCaps = (track.getCapabilities?.() ?? {}) as ExtendedCameraCapabilities;
      pushDebug(
        `final: ${finalSettings.width}×${finalSettings.height} zoom=${finalSettings.zoom ?? '—'}`
      );
      pushDebug(
        `caps: torch=${!!finalCaps.torch} zoom=${
          finalCaps.zoom ? `${finalCaps.zoom.min}-${finalCaps.zoom.max}` : '—'
        }`
      );

      const video = videoRef.current;
      if (!video) {
        throw new Error('Video element not mounted');
      }

      video.srcObject = stream;
      video.setAttribute('playsinline', 'true');
      video.playsInline = true;
      video.muted = true;
      await video.play();

      await applyCameraFeatures(track);

      setIsCameraActive(true);
      setIsScanning(false);
      setIsSearchingCamera(false);

      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(scanFrame);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : typeof err === 'string' ? err : 'Unknown error';
      console.error('useBarcodeScanner: start failed', err);
      setError(`Unable to start scanner: ${message}`);
      setIsCameraActive(false);
      setIsScanning(false);
      setIsSearchingCamera(false);

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    } finally {
      startingRef.current = false;
    }
  }, [scanFrame]);

  const toggleTorch = useCallback(async () => {
    const track = getTrack();
    if (!track || !isCameraActiveRef.current) return;

    const nextValue = !isTorchOnRef.current;
    try {
      await applyTrackConstraints(track, { torch: nextValue });
      setIsTorchOn(nextValue);
    } catch (err) {
      console.error('Error toggling torch:', err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      await new Promise((r) => setTimeout(r, 0));
      if (!cancelled) await start();
    };
    init();

    const handleUnload = () => {
      stop();
    };

    const handlePopState = () => {
      if (isCameraActiveRef.current) {
        stop();
      } else if (restartOnPopstatePath && window.location.pathname === restartOnPopstatePath) {
        start();
      } else {
        stop();
      }
    };

    const handleMessage = (e: MessageEvent) => {
      if (e.data?.action === 'release_camera_and_close_modal') {
        onReleaseRequestRef.current?.();
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('message', handleMessage);

    return () => {
      cancelled = true;
      stop();
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    videoRef,
    isCameraActive,
    isScanning,
    isSearchingCamera,
    isTorchSupported,
    isTorchOn,
    error,
    debugLines,
    toggleTorch,
    stop,
  };
};
